import React from 'react';
import { Box } from '@mui/material';
import { Icon, Table } from 'src/components';
import { GridRenderCellParams, GridCellParams } from '@mui/x-data-grid';
import Tooltip from '@mui/material/Tooltip';
import { themeColors } from 'src/theme';
import { splitCamelCase } from 'src/utils/utils';
import { InvalidUser } from '../validateUsersCsv';

interface InvalidUsersTableProps {
  invalidUsers: InvalidUser[];
}

const renderErrorCell = (params: GridRenderCellParams, key: string, themeColors: any) => {
  const hasError = params.row.errors?.[key];
  const isEmpty = params.value === undefined || params.value === null || params.value === '';
  if (!hasError) return null;
  return (
    <Tooltip title={hasError} placement="top" arrow>
      <Box
        sx={{
          width: '100%',
          height: '100%',
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          backgroundColor: themeColors.redLight,
          cursor: 'pointer',
        }}
      >
        {!isEmpty && (
          <span
            style={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              display: 'block',
              flex: 1,
              minWidth: 0,
              marginRight: 8,
            }}
          >
            {params.value}
          </span>
        )}
        <Box
          sx={{
            position: 'absolute',
            right: 0,
            top: '50%',
            transform: 'translateY(-50%)',
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <Icon name="exclamationRed" />
        </Box>
      </Box>
    </Tooltip>
  );
};

const InvalidUsersTable: React.FC<InvalidUsersTableProps> = ({ invalidUsers }) => {
  const invalidUserColumns =
    invalidUsers.length > 0
      ? Object.keys(invalidUsers[0])
          .filter((key) => key !== 'id' && key !== 'errors')
          .map((key) => ({
            field: key,
            headerName: key === 'rowNumber' ? '#' : splitCamelCase(key),
            width: key === 'rowNumber' ? 60 : key === 'email' ? 240 : 200,
            type: 'string',
            cellClassName: (params: GridCellParams) => (params.row.errors?.[key] ? 'error-cell' : ''),
            renderCell: (params: GridRenderCellParams) => {
              const hasError = params.row.errors?.[key];
              if (hasError) {
                return renderErrorCell(params, key, themeColors);
              }
              return (
                <Box
                  sx={{
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  {params.value}
                </Box>
              );
            },
          }))
      : [];

  return (
    <Box
      sx={{
        height: '100%',
        '& .MuiDataGrid-cell.error-cell': {
          backgroundColor: themeColors.redLight,
        },
      }}
    >
      <Table
        type="advanced"
        columns={invalidUserColumns}
        rows={invalidUsers}
        toolbarProps={{ options: { quickFilter: true } }}
        sx={{ height: '100%' }}
      />
    </Box>
  );
};

export default InvalidUsersTable;
